import { APP } from 'config';
import { currencies, Currency } from 'types/general';
import { formatCurrency, formatPercents } from 'utils/format';
import { includes, toCents } from 'utils/general';
import { DiscountOfferType, discountOfferTypes } from './discounts.types';

function discountCurrency(currency: string | null): Currency {
  return currency != null && includes(currencies, currency)
    ? currency
    : APP.currencies.default;
}

export function formatDiscountAmount(
  language: string,
  amount: number,
  currency: string | null,
): string {
  return formatCurrency(language, toCents(amount), discountCurrency(currency));
}

export function discountOfferTypeFromApi(
  value: string | null | undefined,
): DiscountOfferType | null {
  if (value && discountOfferTypes.includes(value as DiscountOfferType)) {
    return value as DiscountOfferType;
  }
  return null;
}

export function discountAmountFromApi(
  value: number | string | null | undefined,
): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function roundDiscountAmount(value: number): number {
  return Math.round(value * 100) / 100;
}

function finiteAmount(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

function roundedAmount(value: number): number | null {
  const amount = finiteAmount(value);
  return amount == null ? null : roundDiscountAmount(amount);
}

export function discountOfferPayload(fields: {
  type: DiscountOfferType | '';
  discountedPercentage: number;
  discountedFixed: number;
  currency: string;
  averageBasketSize: number;
  averageSaving: number;
}) {
  const type = discountOfferTypeFromApi(fields.type);
  const currency = fields.currency || null;

  switch (type) {
    case 'PERCENTAGE': {
      const discountedPercentage = finiteAmount(fields.discountedPercentage);
      const averageBasketSize = roundedAmount(fields.averageBasketSize);
      return {
        type,
        discountedPercentage,
        discountedFixed: null,
        currency,
        averageBasketSize,
        averageSaving:
          averageBasketSize != null && discountedPercentage != null
            ? roundDiscountAmount(
                (averageBasketSize * discountedPercentage) / 100,
              )
            : null,
      };
    }
    case 'FIXED': {
      const discountedFixed = roundedAmount(fields.discountedFixed);
      return {
        type,
        discountedPercentage: null,
        discountedFixed,
        currency,
        averageBasketSize: null,
        averageSaving: discountedFixed,
      };
    }
    case 'SPECIAL_OFFER':
      return {
        type,
        discountedPercentage: null,
        discountedFixed: null,
        currency,
        averageBasketSize: null,
        averageSaving: roundedAmount(fields.averageSaving),
      };
    case null:
      return {
        type,
        discountedPercentage: null,
        discountedFixed: null,
        currency,
        averageBasketSize: null,
        averageSaving: null,
      };
    default:
      return type satisfies never;
  }
}

export function discountOfferSummary(
  language: string,
  discount: {
    type: DiscountOfferType | null;
    discountedPercentage: number | null;
    discountedFixed: number | null;
    currency: string | null;
  },
  specialOfferLabel: string,
): string | null {
  switch (discount.type) {
    case 'PERCENTAGE':
      return discount.discountedPercentage == null
        ? null
        : formatPercents(language, discount.discountedPercentage / 100);
    case 'FIXED':
      return discount.discountedFixed == null
        ? null
        : formatDiscountAmount(
            language,
            discount.discountedFixed,
            discount.currency,
          );
    case 'SPECIAL_OFFER':
      return specialOfferLabel;
    case null:
      return null;
    default:
      return discount.type satisfies never;
  }
}
